import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../icons/Icons';
import styles from './Button.module.css';

// --- Color helpers ---------------------------------------------------------
// Convert hex to RGB
function hexToRgb(hex) {
  if (typeof hex !== 'string') throw new Error('Invalid color');
  // Early reject CSS variables or non-hex patterns so we can gracefully fallback
  if (!hex.trim().startsWith('#')) throw new Error('Not a hex color');
  let clean = hex.trim().replace('#', '').toLowerCase();

  // Support 3 or 6 (RGB) and 4 or 8 (RGBA) digits by ignoring alpha channel
  if (clean.length === 3) {
    clean = clean.split('').map((c) => c + c).join('');
  } else if (clean.length === 4) {
    // #RGBA -> expand and drop alpha
    const [r, g, b] = clean.slice(0, 3).split('').map((c) => c + c);
    clean = r + g + b;
  } else if (clean.length === 8) {
    // #RRGGBBAA -> drop alpha
    clean = clean.slice(0, 6);
  } else if (clean.length !== 6) {
    throw new Error('Unsupported hex length');
  }

  if (!/^[0-9a-f]{6}$/i.test(clean)) throw new Error('Invalid hex digits');

  const bigint = parseInt(clean, 16);
  if (Number.isNaN(bigint)) throw new Error('Invalid hex parse');

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}

function clamp(v, min = 0, max = 255) { return Math.min(max, Math.max(min, v)); }

// Shade color by percentage (negative to darken, positive to lighten)
function shadeColor(hex, percent) {
  try {
    const { r, g, b } = hexToRgb(hex);
    const amt = Math.round(2.55 * percent);
    const R = clamp(r + amt);
    const G = clamp(g + amt);
    const B = clamp(b + amt);
    return rgbToHex(R, G, B);
  } catch {
    return hex; // fallback to input if parsing fails
  }
}

// Relative luminance to decide on text color
function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function pickTextColor(bgHex) {
  try {
    // Use WCAG heuristic: threshold ~0.179
    const lum = getLuminance(bgHex);
    return lum > 0.179 ? '#0C1622' : '#FFFFFF';
  } catch {
    return '#FFFFFF';
  }
}

/**
 * Reusable Button component
 *
 * Props
 * - variant: 'primary' | 'secondary' | 'outline' | 'outline-neutral' | 'ghost'
 * - size: 'sm' | 'md' | 'lg'
 * - iconLeft: { name: string, color?: string, width?: number|string, height?: number|string, title?: string }
 * - iconRight: same as iconLeft
 * - loading: boolean
 * - disabled: boolean
 * - fullWidth: boolean
 * - onClick: (e) => void
 * - type: 'button' | 'submit' | 'reset'
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  loading = false,
  disabled = false,
  fullWidth = false,
  color, // optional base color to drive bg and state colors
  iconOnly = false,
  ariaLabel,
  textColor, // optional explicit foreground/text color override
  onClick,
  type = 'button',
  className = '',
  ...rest
}) => {
  const isDisabled = disabled || loading;

  const variantClass =
    variant === 'secondary' ? styles.secondary :
    variant === 'outline' ? styles.outline :
    variant === 'outline-neutral' ? styles.outlineNeutral :
    variant === 'ghost' ? styles.ghost : undefined; // primary uses defaults

  const sizeClass = size === 'sm' ? styles.sm : size === 'lg' ? styles.lg : styles.md;

  const classes = [
    styles.btn,
    sizeClass,
    variantClass,
    iconOnly ? styles.iconOnly : null,
    fullWidth ? styles.block : null,
    isDisabled ? styles.disabled : null,
    className || null,
  ].filter(Boolean).join(' ');

  // Inline CSS variables to override theme colors
  let styleVars = {};
  if (color) {
    const isHex = typeof color === 'string' && color.trim().startsWith('#');
    if (isHex) {
      const base = color;
      const hover = shadeColor(color, -10); // slightly darker on hover (user-adjusted)
      const active = shadeColor(color, -15); // more dark on active (user-adjusted)
      const text = pickTextColor(base);
      styleVars['--btn-bg'] = base;
      styleVars['--btn-bg-hover'] = hover;
      styleVars['--btn-bg-active'] = active;
      // default text from contrast if not explicitly provided later
      styleVars['--btn-fg'] = text;
    } else {
      // Assume CSS variable or other valid CSS color string; use CSS color-mix to derive states
      const base = color;
      // Darken by mixing with black (fallback-friendly)
      const hover = `color-mix(in srgb, ${base} 90%, black)`;
      const active = `color-mix(in srgb, ${base} 80%, black)`;
      styleVars['--btn-bg'] = base;
      styleVars['--btn-bg-hover'] = hover;
      styleVars['--btn-bg-active'] = active;
      // Do not set --btn-fg by default; allow theme or explicit textColor to control it
    }
  }

  // Explicit text color override always wins, even without background color prop
  if (textColor) {
    styleVars['--btn-fg'] = textColor;
  }

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={isDisabled}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      aria-label={iconOnly ? (ariaLabel || iconLeft?.title || iconRight?.title || undefined) : undefined}
      style={Object.keys(styleVars).length ? styleVars : undefined}
      {...rest}
    >
      {loading && (
        <span className={styles.spinner} aria-hidden="true" />
      )}
      {iconOnly ? (
        // Icon-only: show a single centered icon, prefer left if both provided
        <span className={styles.icon} aria-hidden="true">
          <Icon
            name={(iconLeft || iconRight)?.name}
            color={(iconLeft || iconRight)?.color || 'currentColor'}
            width={(iconLeft || iconRight)?.width || 24}
            height={(iconLeft || iconRight)?.height || 24}
            title={(iconLeft || iconRight)?.title}
          />
        </span>
      ) : (
      <>
      {iconLeft && (
        <span className={`${styles.icon} ${styles.iconLeft}`} aria-hidden="true">
          <Icon
            name={iconLeft.name}
            color={iconLeft.color || 'currentColor'}
            width={iconLeft.width || 24}
            height={iconLeft.height || 24}
            title={iconLeft.title}
          />
        </span>
      )}
      <span className={styles.label}>{children}</span>
      {iconRight && (
        <span className={`${styles.icon} ${styles.iconRight}`} aria-hidden="true">
          <Icon
            name={iconRight.name}
            color={iconRight.color || 'currentColor'}
            width={iconRight.width || 24}
            height={iconRight.height || 24}
            title={iconRight.title}
          />
        </span>
      )}
      </>
      )}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'outline-neutral', 'ghost']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  iconLeft: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
  }),
  iconRight: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
  }),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  color: PropTypes.string,
  textColor: PropTypes.string,
  iconOnly: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default Button;
export { Button };
